import moment from 'moment';
import { useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  exportStatementOfChangesInEquityReport,
  getStatementOfChangesInEquityReport,
} from '../../api/statementOfChangesInEquity';
import DefaultLayout from '../../components/Layouts/DefaultLayout';
import useNavActive from '../../components/Navbar/useNavActive';
import PrimaryDatePicker from '../../components/PrimaryDatePicker';
import { numberFormatCurrency } from '../../utils/helper';
import ComputingPreloader from './ComputingPreloader';

const defaultFilter = {
  date_from: moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
  date_to: moment().format('YYYY-MM-DD'),
};

const defaultLoaderMessage = 'Computing report...';
const defaultExportLoaderMessage = 'Exporting report...';

const clampProgress = (progress) => {
  const numericProgress = Number(progress);

  if (Number.isNaN(numericProgress)) {
    return 0;
  }

  return Math.min(Math.max(numericProgress, 0), 100);
};

const delay = (milliseconds) => {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

const parseProgressResponse = (responseData, fallbackMessage) => {
  const response = responseData?.data?.response ?? responseData?.data ?? {};
  const { code, result } = response;

  if (code && code !== '00000') {
    throw new Error(result);
  }

  return {
    result,
    message: result?.message ?? fallbackMessage,
    progress: clampProgress(result?.progress),
  };
};

const parseReportResponse = (responseData) => {
  const { result, message, progress } = parseProgressResponse(
    responseData,
    defaultLoaderMessage,
  );
  const reportResult = result?.result ?? result ?? {};
  const columns = reportResult?.columns ?? reportResult?.headers ?? [];
  const rows =
    reportResult?.rows ??
    reportResult?.data ??
    (Array.isArray(reportResult) ? reportResult : []);
  const totals = reportResult?.totals ?? null;

  return {
    columns,
    rows,
    totals,
    message,
    progress,
  };
};

const extractExportUrl = (result) => {
  if (!result) {
    return null;
  }

  if (typeof result === 'string') {
    return result;
  }

  return (
    result.url ??
    result.file_url ??
    result.file ??
    result.download_url ??
    result.result ??
    null
  );
};

const getColumnKey = (column) => {
  if (typeof column === 'string') {
    return column;
  }

  return column.value ?? column.key ?? column.field ?? column.name;
};

const getColumnLabel = (column) => {
  if (typeof column === 'string') {
    return column;
  }

  return column.label ?? column.header ?? column.title ?? column.name;
};

const labelKeys = [
  'label',
  'description',
  'particular',
  'particulars',
  'row_label',
  'name',
  'title',
  'account',
  'account_name',
  'line_item',
];

const defaultRowLabels = [
  ({ filter }) => `At ${moment(filter.date_from).format('MMMM DD, YYYY')}`,
  () => 'Issuance of Shares',
  () => 'Net Income (Loss) during the Period',
  () => 'Transfer of Income to RE',
  () => 'Dividends Declaration',
  () => 'Reclass of RE to Cumulative Bitpoints Earnings',
  ({ filter }) => `At ${moment(filter.date_to).format('MMMM DD, YYYY')}`,
];

const tableStyles = {
  header: {
    borderBottom: '3px solid #444',
    color: '#333',
    fontSize: '14px',
    fontWeight: 700,
    padding: '10px 8px',
    textTransform: 'none',
  },
  labelCell: {
    color: '#333',
    fontSize: '14px',
    padding: '7px 8px',
  },
  amountCell: {
    color: '#333',
    fontSize: '14px',
    padding: '7px 8px',
    textAlign: 'right',
  },
};

const formatHeaderLabel = (label) => {
  if (!label) {
    return '';
  }

  const labelText = String(label).replaceAll('_', ' ');

  if (labelText !== labelText.toUpperCase()) {
    return labelText;
  }

  const lowerCased = labelText.toLowerCase();

  return lowerCased.charAt(0).toUpperCase() + lowerCased.slice(1);
};

const formatAmountValue = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return '-';
  }

  if (!Number.isNaN(Number(value)) && value !== true && value !== false) {
    if (Number(value) === 0) {
      return '-';
    }

    return numberFormatCurrency(value);
  }

  return value;
};

const getRowLabelKey = (row, columns) => {
  const columnKeys = columns.map(getColumnKey);
  const rowKeys = Object.keys(row ?? {});
  const matchingLabelKey = labelKeys.find((key) => rowKeys.includes(key));

  if (matchingLabelKey) {
    return matchingLabelKey;
  }

  return rowKeys.find((key) => !columnKeys.includes(key));
};

const getRowLabel = ({ row, index, rowLabelKey, filter }) => {
  if (rowLabelKey && row[rowLabelKey]) {
    return row[rowLabelKey];
  }

  if (defaultRowLabels[index]) {
    return defaultRowLabels[index]({ filter });
  }

  return '';
};

const isTotalColumn = (column) => {
  return String(getColumnKey(column) ?? getColumnLabel(column))
    .toLowerCase()
    .includes('total');
};

const isDividendsColumn = (column) => {
  return String(getColumnKey(column) ?? getColumnLabel(column))
    .toLowerCase()
    .includes('dividend');
};

const sumColumnValues = (rows, columnKey) => {
  return rows.reduce((total, row) => total + Number(row[columnKey] ?? 0), 0);
};

const buildEndingBalanceRow = ({ rows, amountColumns, filter }) => {
  const endingBalanceRow = {
    _row_label: `At ${moment(filter.date_to).format('MMMM DD, YYYY')}`,
  };

  amountColumns.forEach((column) => {
    const columnKey = getColumnKey(column);

    if (isTotalColumn(column)) {
      return;
    }

    endingBalanceRow[columnKey] = sumColumnValues(rows, columnKey);
  });

  amountColumns.forEach((column) => {
    const columnKey = getColumnKey(column);

    if (!isTotalColumn(column)) {
      return;
    }

    endingBalanceRow[columnKey] = amountColumns.reduce(
      (total, amountColumn) => {
        if (isTotalColumn(amountColumn)) {
          return total;
        }

        const amountColumnKey = getColumnKey(amountColumn);
        const amount = Number(endingBalanceRow[amountColumnKey] ?? 0);

        return isDividendsColumn(amountColumn)
          ? total - amount
          : total + amount;
      },
      0,
    );
  });

  return endingBalanceRow;
};

const isTotalRow = (row, index, rows) => {
  const labelValue = String(
    row._row_label ?? row.label ?? row.description ?? row.particulars ?? '',
  );

  return (
    index === 0 ||
    index === rows.length - 1 ||
    row.is_total ||
    row.isTotal ||
    labelValue.toLowerCase().startsWith('at ')
  );
};

const StatementOfChangesInEquity = () => {
  const [filter, setFilter] = useState(defaultFilter);
  const [isGenerated, setIsGenerated] = useState(false);
  const [exportState, setExportState] = useState({
    loading: false,
    message: defaultExportLoaderMessage,
    progress: 0,
  });
  const [reportData, setReportData] = useState({
    columns: [],
    rows: [],
    totals: null,
    loading: false,
    message: defaultLoaderMessage,
    progress: 0,
  });

  useNavActive([
    'finance_and_accounting',
    'fna_financial_reports',
    'statement_of_changes_in_equity',
  ]);

  const handleDateChange = ({ name, value }) => {
    setFilter({
      ...filter,
      [name]: value,
    });
  };

  const pollReport = async (params) => {
    const res = await getStatementOfChangesInEquityReport(params);
    const { columns, rows, totals, message, progress } =
      parseReportResponse(res);
    const isComplete = progress >= 100;

    setReportData({
      columns: [],
      rows: [],
      totals: null,
      loading: true,
      message: isComplete ? 'Finalizing...' : message,
      progress,
    });

    if (!isComplete) {
      await delay(1000);

      return pollReport(params);
    }

    await delay(3000);

    setReportData({
      columns,
      rows,
      totals,
      loading: false,
      message,
      progress,
    });

    return { columns, rows, totals };
  };

  const handleGenerate = async () => {
    const generatedFilter = {
      from_date: filter.date_from,
      to_date: filter.date_to,
    };

    setIsGenerated(true);
    setReportData({
      columns: [],
      rows: [],
      totals: null,
      loading: true,
      message: defaultLoaderMessage,
      progress: 0,
    });

    try {
      await pollReport(generatedFilter);
    } catch (error) {
      setReportData({
        columns: [],
        rows: [],
        totals: null,
        loading: false,
        message: defaultLoaderMessage,
        progress: 0,
      });
      toast.error(error.message);
    }
  };

  const pollExport = async (params) => {
    const res = await exportStatementOfChangesInEquityReport(params);
    const { result, message, progress } = parseProgressResponse(
      res,
      defaultExportLoaderMessage,
    );
    const isComplete = progress >= 100;

    setExportState({
      loading: true,
      message: isComplete ? 'Downloading your export...' : message,
      progress,
    });

    if (!isComplete) {
      await delay(1000);

      return pollExport(params);
    }

    await delay(3000);

    return extractExportUrl(result);
  };

  const handleExport = async () => {
    setExportState({
      loading: true,
      message: defaultExportLoaderMessage,
      progress: 0,
    });

    try {
      const downloadUrl = await pollExport({
        from_date: filter.date_from,
        to_date: filter.date_to,
      });

      if (!downloadUrl) {
        throw new Error('Export failed.');
      }

      window.location.href = downloadUrl;
    } catch (error) {
      toast.error(error.message ?? 'Export failed.');
    } finally {
      setExportState({
        loading: false,
        message: defaultExportLoaderMessage,
        progress: 0,
      });
    }
  };

  const reportColumns = reportData.columns.length
    ? reportData.columns
    : Object.keys(reportData.rows[0] ?? {}).map((key) => ({
        label: key,
        value: key,
      }));
  const rowLabelKey = getRowLabelKey(reportData.rows[0], reportColumns);
  const amountColumns = rowLabelKey
    ? reportColumns.filter((column) => getColumnKey(column) !== rowLabelKey)
    : reportColumns;
  const rowsWithLabels = reportData.rows.map((row, index) => ({
    ...row,
    _row_label: getRowLabel({ row, index, rowLabelKey, filter }),
  }));

  const endingBalanceLabel = `At ${moment(filter.date_to).format(
    'MMMM DD, YYYY',
  )}`;
  const hasEndingBalanceRow = rowsWithLabels.some(
    (row) =>
      String(row._row_label).toLowerCase() === endingBalanceLabel.toLowerCase(),
  );
  const endingBalanceRow = reportData.totals
    ? {
        ...reportData.totals,
        _row_label: reportData.totals.label ?? endingBalanceLabel,
      }
    : buildEndingBalanceRow({ rows: rowsWithLabels, amountColumns, filter });

  const displayRows =
    rowsWithLabels.length && !hasEndingBalanceRow
      ? [...rowsWithLabels, endingBalanceRow]
      : rowsWithLabels;

  return (
    <DefaultLayout
      classHeader="dlc-header-block mt-0 pb-0 mb-0"
      title={
        <span
          style={{
            fontSize: '0.92rem',
            fontWeight: 400,
            letterSpacing: '0.03em',
          }}
        >
          <span style={{ color: '#999' }}>Finance and Accounting</span>
          <span style={{ color: '#bbb', margin: '0 6px' }}>›</span>
          <span style={{ color: '#999' }}>Financial Reports</span>
          <span style={{ color: '#bbb', margin: '0 6px' }}>›</span>
          <span style={{ color: '#333', fontWeight: 600 }}>
            Statement of Changes in Equity
          </span>
        </span>
      }
    >
      <div
        className="white-box mb-3"
        style={{ padding: '20px', marginTop: '8px' }}
      >
        <div className="row align-items-end">
          <div className="col-md-2">
            <label>Date From</label>
            <label className="req">*</label>
            <PrimaryDatePicker
              name="date_from"
              value={filter.date_from}
              onChange={(date) =>
                handleDateChange({ name: 'date_from', value: date })
              }
              minDate="2022-11-30"
              maxDate={moment().format('YYYY-MM-DD')}
              disabled={reportData.loading || exportState.loading}
            />
          </div>
          <div className="col-md-2">
            <label>Date To</label>
            <label className="req">*</label>
            <PrimaryDatePicker
              name="date_to"
              value={filter.date_to}
              onChange={(date) =>
                handleDateChange({ name: 'date_to', value: date })
              }
              disabled={reportData.loading || exportState.loading}
            />
          </div>
          <div className="col-md-2">
            <button
              type="button"
              className="btn btn-success"
              onClick={handleGenerate}
              disabled={reportData.loading || exportState.loading}
            >
              Generate
            </button>
          </div>
          <div className="col-md-6 text-right">
            <button
              type="button"
              className="btn btn-info"
              disabled={
                reportData.loading || exportState.loading || !displayRows.length
              }
              onClick={handleExport}
            >
              {exportState.loading ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>
      {isGenerated && (
        <div className="white-box" style={{ minHeight: '260px' }}>
          {reportData.loading ? (
            <ComputingPreloader
              message={reportData.message}
              progress={reportData.progress}
            />
          ) : exportState.loading ? (
            <ComputingPreloader
              message={exportState.message}
              progress={exportState.progress}
            />
          ) : !reportData.rows.length ? (
            <div className="p-3">
              <Alert variant="warning">No Data.</Alert>
            </div>
          ) : (
            <>
              <center>
                <b>Bell-Kenz Pharma Inc.</b>
                <br />
                <b>Statement of Changes in Shareholder's Equity</b>
                <br />
                <b>As of {moment(filter.date_to).format('MMMM D, YYYY')} </b>
                <br />
                <br />
              </center>
              <div className="table-responsive">
                <Table className="mb-0">
                  <thead>
                    <tr>
                      <th style={tableStyles.header}>&nbsp;</th>
                      {amountColumns.map((column) => (
                        <th
                          key={getColumnKey(column)}
                          style={{
                            ...tableStyles.header,
                            textAlign: 'right',
                          }}
                        >
                          {formatHeaderLabel(getColumnLabel(column))}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, index) => {
                      const isEmphasized = isTotalRow(row, index, displayRows);
                      const rowBackgroundColor = index % 2 ? '#e9e9e9' : '#fff';
                      const stripedCellStyle = {
                        '--bs-table-bg': rowBackgroundColor,
                        '--bs-table-accent-bg': rowBackgroundColor,
                        backgroundColor: rowBackgroundColor,
                        boxShadow: `inset 0 0 0 9999px ${rowBackgroundColor}`,
                      };

                      return (
                        <tr
                          key={index}
                          style={{
                            borderTop: isEmphasized ? '2px solid #444' : '',
                            borderBottom: isEmphasized ? '2px solid #444' : '',
                            fontWeight: isEmphasized ? 700 : 400,
                          }}
                        >
                          <td
                            style={{
                              ...tableStyles.labelCell,
                              ...stripedCellStyle,
                              fontWeight: isEmphasized ? 700 : 400,
                            }}
                          >
                            {formatAmountValue(row._row_label)}
                          </td>
                          {amountColumns.map((column) => {
                            const columnKey = getColumnKey(column);

                            return (
                              <td
                                key={columnKey}
                                style={{
                                  ...tableStyles.amountCell,
                                  ...stripedCellStyle,
                                  fontWeight:
                                    isEmphasized || isTotalColumn(column)
                                      ? 700
                                      : 400,
                                }}
                              >
                                {formatAmountValue(row[columnKey])}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}
    </DefaultLayout>
  );
};

export default StatementOfChangesInEquity;
